/**
 * PluginManagerUI.tsx - Plugin management interface component
 * 
 * CHANGE HISTORY:
 * - 251101_1622 [pending] danielbeer: Initial creation - first management UI component
 * 
 * ARCHITECTURE NOTE:
 * This is the FIRST example of the management interface pattern.
 * All utility packages (Pluggr, Cachr, Searchr, etc.) should follow this pattern:
 * - Package provides logic/manager class
 * - Interfacr provides UI component
 * - Apps wire them together
 * 
 * Pattern:
 * - Pluggr exports PluginManager (logic)
 * - Interfacr exports PluginManagerUI (UI)
 * - Apps import both and connect them
 */

import React, { useState, useEffect } from 'react';
import './PluginManagerUI.css';

/**
 * Props for PluginManagerUI component
 * 
 * Generic pattern: Accept manager instance from utility package
 */
export interface PluginManagerUIProps {
  /** Plugin manager instance from @opcentral/pluggr */
  manager: any; // TODO: Type as PluginManager when Pluggr is built
  /** Optional: Custom title */
  title?: string;
  /** Optional: Show advanced options */
  showAdvanced?: boolean;
  /** Optional: Callback when plugin loaded */
  onPluginLoaded?: (plugin: any) => void;
  /** Optional: Callback when plugin unloaded */
  onPluginUnloaded?: (pluginId: string) => void;
}

/**
 * Plugin Management UI Component
 * 
 * Provides standard interface for managing plugins across all apps.
 * Follows OpCentral pattern: UI in Interfacr, logic in utility package.
 * 
 * @example
 * ```tsx
 * import { PluginManager } from '@opcentral/pluggr';
 * import { PluginManagerUI } from '@opcentral/interfacr';
 * 
 * const manager = new PluginManager();
 * 
 * function MyApp() {
 *   return <PluginManagerUI manager={manager} />;
 * }
 * ```
 */
export function PluginManagerUI({
  manager,
  title = 'Plugin Management',
  showAdvanced = false,
  onPluginLoaded,
  onPluginUnloaded
}: PluginManagerUIProps) {
  const [plugins, setPlugins] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPluginUrl, setNewPluginUrl] = useState('');

  // Load plugins on mount
  useEffect(() => {
    if (manager && typeof manager.getPlugins === 'function') {
      setPlugins(manager.getPlugins());
    }
  }, [manager]);

  const handleLoadPlugin = async () => {
    if (!newPluginUrl.trim()) {
      setError('Please enter a plugin URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const plugin = await manager.loadPlugin(newPluginUrl);
      setPlugins(manager.getPlugins());
      setNewPluginUrl('');
      onPluginLoaded?.(plugin);
    } catch (err: any) {
      setError(err.message || 'Failed to load plugin');
    } finally {
      setLoading(false);
    }
  };

  const handleUnloadPlugin = async (pluginId: string) => {
    setLoading(true);
    setError(null);

    try {
      await manager.unloadPlugin(pluginId);
      setPlugins(manager.getPlugins());
      onPluginUnloaded?.(pluginId);
    } catch (err: any) {
      setError(err.message || 'Failed to unload plugin');
    } finally {
      setLoading(false);
    }
  };

  const handleEnablePlugin = async (pluginId: string) => {
    try {
      await manager.enablePlugin(pluginId);
      setPlugins(manager.getPlugins());
    } catch (err: any) {
      setError(err.message || 'Failed to enable plugin');
    }
  };

  const handleDisablePlugin = async (pluginId: string) => {
    try {
      await manager.disablePlugin(pluginId);
      setPlugins(manager.getPlugins());
    } catch (err: any) {
      setError(err.message || 'Failed to disable plugin');
    }
  };

  return (
    <div className="plugin-manager-ui">
      <div className="plugin-manager-header">
        <h2>{title}</h2>
        <div className="plugin-count">
          {plugins.length} plugin{plugins.length !== 1 ? 's' : ''} loaded
        </div>
      </div>

      {error && (
        <div className="plugin-manager-error">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="plugin-manager-load">
        <h3>Load New Plugin</h3>
        <div className="load-plugin-form">
          <input
            type="text"
            placeholder="Plugin URL, file path, or npm package"
            value={newPluginUrl}
            onChange={(e) => setNewPluginUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLoadPlugin()}
            disabled={loading}
          />
          <button onClick={handleLoadPlugin} disabled={loading || !newPluginUrl.trim()}>
            {loading ? 'Loading...' : 'Load Plugin'}
          </button>
        </div>
        <div className="load-plugin-help">
          <p>Supported sources:</p>
          <ul>
            <li><strong>URL:</strong> https://example.com/plugin.js</li>
            <li><strong>File:</strong> /path/to/plugin.js</li>
            <li><strong>NPM:</strong> @scope/plugin-name</li>
          </ul>
        </div>
      </div>

      <div className="plugin-manager-list">
        <h3>Installed Plugins</h3>
        {plugins.length === 0 ? (
          <div className="no-plugins">
            <p>No plugins loaded</p>
            <p className="hint">Load a plugin using the form above</p>
          </div>
        ) : (
          <div className="plugins-grid">
            {plugins.map((plugin) => (
              <div key={plugin.id} className={`plugin-card ${plugin.enabled ? 'enabled' : 'disabled'}`}>
                <div className="plugin-header">
                  <div className="plugin-info">
                    <h4>{plugin.name || plugin.id}</h4>
                    <span className="plugin-version">{plugin.version || 'unknown'}</span>
                  </div>
                  <div className="plugin-status">
                    <span className={`status-badge ${plugin.enabled ? 'active' : 'inactive'}`}>
                      {plugin.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                {plugin.description && (
                  <p className="plugin-description">{plugin.description}</p>
                )}

                {showAdvanced && (
                  <div className="plugin-details">
                    <div className="detail-row">
                      <span className="detail-label">Source:</span>
                      <span className="detail-value">{plugin.source || 'unknown'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Type:</span>
                      <span className="detail-value">{plugin.type || 'unknown'}</span>
                    </div>
                    {plugin.author && (
                      <div className="detail-row">
                        <span className="detail-label">Author:</span>
                        <span className="detail-value">{plugin.author}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="plugin-actions">
                  {plugin.enabled ? (
                    <button
                      className="btn-secondary"
                      onClick={() => handleDisablePlugin(plugin.id)}
                      disabled={loading}
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      className="btn-primary"
                      onClick={() => handleEnablePlugin(plugin.id)}
                      disabled={loading}
                    >
                      Enable
                    </button>
                  )}
                  <button
                    className="btn-danger"
                    onClick={() => handleUnloadPlugin(plugin.id)}
                    disabled={loading}
                  >
                    Unload
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PluginManagerUI;
